import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { LANDING } from "@/constants/testIds";

/**
 * NotFoundPage (404) — replaces the catch-all redirect to home.
 * Renders inside the existing dark institutional shell.
 */
const NotFoundPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />
      <main
        data-testid={LANDING.notFoundPage}
        className="relative bg-[var(--kr-bg)] min-h-[80vh] flex items-center"
      >
        <div className="max-w-[1480px] w-full mx-auto px-6 lg:px-12 py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-4 mb-8">
                <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                  404
                </span>
                <span className="w-12 h-px bg-[var(--kr-gold)]/60" />
                <span className="font-mono-label text-[10px] text-[var(--kr-text-muted)]">
                  Page not located
                </span>
              </div>
              <h1 className="font-serif-display text-[var(--kr-text)] text-[clamp(2.4rem,5vw,4.6rem)] leading-[1.04] tracking-[-0.01em] mb-8">
                The page you requested is{" "}
                <span className="italic text-[var(--kr-gold)]">
                  not catalogued
                </span>
                .
              </h1>
              <p className="text-[var(--kr-text-muted)] text-[17px] leading-[1.85] max-w-[58ch] mb-12">
                This may be a stale link, a mistyped URL, or material that has
                been moved into a documented archive. The partnership
                consolidates public materials at the routes listed alongside.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/"
                  data-testid={LANDING.notFoundHome}
                  className="kr-btn-primary"
                >
                  Return to Homepage
                  <span aria-hidden>→</span>
                </Link>
                <Link to="/contact" className="kr-btn-ghost">
                  Report a broken link
                </Link>
              </div>
            </div>

            <aside className="lg:col-span-5 lg:pl-8">
              <div className="border border-[var(--kr-line)] p-8 lg:p-10 bg-[var(--kr-bg-2)]">
                <div className="font-mono-label text-[10px] text-[var(--kr-gold)] mb-6">
                  Documented Routes
                </div>
                <ul className="space-y-3">
                  {[
                    ["Strategies", "/strategies"],
                    ["Fund Architecture", "/funds"],
                    ["Perspectives", "/perspectives"],
                    ["Team", "/team"],
                    ["Responsible Capital", "/responsible-capital"],
                    ["Newsroom", "/news"],
                    ["Press Kit", "/press-kit"],
                    ["Contact", "/contact"],
                  ].map(([label, to]) => (
                    <li key={to}>
                      <Link
                        to={to}
                        className="flex items-center justify-between py-3 border-b border-[var(--kr-line)] text-[var(--kr-text)] text-[14.5px] hover:text-[var(--kr-gold)] transition-colors"
                      >
                        <span>{label}</span>
                        <span
                          className="font-mono-label text-[10px] text-[var(--kr-text-muted)]"
                          aria-hidden
                        >
                          →
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default NotFoundPage;
